import * as React from "react";
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { API_ADMIN_BASE_URL } from "@/lib/api-client";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SidebarTrigger } from "@/components/ui/sidebar";
import {
  Users, Layout, Activity, Wallet, Bot, HardDrive, AlertTriangle,
} from "lucide-react";

interface DashboardData {
  users: { total: number; newThisWeek: number; activeToday: number };
  canvases: { total: number; createdThisWeek: number };
  jobs: { runningNow: number; completedToday: number; failedToday: number; stuckCount: number };
  wallet: { totalBalance: number; topUpsToday: number; deductionsToday: number };
  models: { total: number; enabled: number };
  storage: { totalFiles: number; totalSizeGB: number };
}

export const Route = createFileRoute('/dashboard/')({
  component: DashboardPage,
})

function DashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = React.useState<DashboardData | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    document.title = "Dashboard - Admin";
    fetchDashboard();
  }, []);

  async function fetchDashboard() {
    try {
      const res = await fetch(`${API_ADMIN_BASE_URL}/api/admin/dashboard`, { credentials: "include" });
      if (!res.ok) return;
      const json = await res.json();
      setData(json.data);
    } catch (error) {
      console.error("Dashboard fetch error:", error);
    } finally {
      setLoading(false);
    }
  }

  const fmt = (n: number) => n.toLocaleString();
  const fmtCurrency = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="bg-background h-full overflow-auto">
      <header>
        <div className="border-border flex h-12 shrink-0 items-center gap-2 border-b px-4 md:h-16 md:px-6">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem><BreadcrumbPage>Dashboard</BreadcrumbPage></BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <div className="border-border border-b px-4 py-4 md:px-6 md:py-6">
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">Platform Overview</h1>
          <p className="text-muted-foreground text-sm">Real-time health snapshot</p>
        </div>
      </header>

      <main className="space-y-6 px-4 py-6 md:px-6">
        {loading ? (
          <p className="text-muted-foreground text-center py-12">Loading dashboard...</p>
        ) : !data ? (
          <p className="text-muted-foreground text-center py-12">Failed to load dashboard data</p>
        ) : (
          <>
            {data.jobs.stuckCount > 0 && (
              <div className="flex items-center gap-3 rounded-lg border border-red-500/50 bg-red-500/10 p-4">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                <div>
                  <p className="font-medium text-red-500">{data.jobs.stuckCount} stuck job{data.jobs.stuckCount > 1 ? "s" : ""}</p>
                  <p className="text-sm text-red-400">Jobs running for more than 10 minutes</p>
                </div>
                <button onClick={() => navigate({ to: "/dashboard/jobs", search: { status: "RUNNING" } })} className="ml-auto text-sm text-red-500 underline">View Jobs</button>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => navigate({ to: "/dashboard/users" })}>
                <CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium">Users</CardTitle><Users className="h-4 w-4 text-muted-foreground" /></CardHeader>
                <CardContent><div className="text-2xl font-bold">{fmt(data.users.total)}</div><p className="text-xs text-muted-foreground">+{fmt(data.users.newThisWeek)} this week · {fmt(data.users.activeToday)} active today</p></CardContent>
              </Card>
              <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => navigate({ to: "/dashboard/canvas" })}>
                <CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium">Canvases</CardTitle><Layout className="h-4 w-4 text-muted-foreground" /></CardHeader>
                <CardContent><div className="text-2xl font-bold">{fmt(data.canvases.total)}</div><p className="text-xs text-muted-foreground">+{fmt(data.canvases.createdThisWeek)} this week</p></CardContent>
              </Card>
              <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => navigate({ to: "/dashboard/jobs" })}>
                <CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium">Jobs</CardTitle><Activity className="h-4 w-4 text-muted-foreground" /></CardHeader>
                <CardContent><div className="text-2xl font-bold">{fmt(data.jobs.runningNow)} running</div><p className="text-xs text-muted-foreground">{fmt(data.jobs.completedToday)} completed · {data.jobs.failedToday > 0 ? <span className="text-red-500">{fmt(data.jobs.failedToday)} failed</span> : "0 failed"} today</p></CardContent>
              </Card>
              <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => navigate({ to: "/dashboard/wallets" })}>
                <CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium">Wallet</CardTitle><Wallet className="h-4 w-4 text-muted-foreground" /></CardHeader>
                <CardContent><div className="text-2xl font-bold">{fmtCurrency(data.wallet.totalBalance)}</div><p className="text-xs text-muted-foreground">+{fmtCurrency(data.wallet.topUpsToday)} / -{fmtCurrency(data.wallet.deductionsToday)} today</p></CardContent>
              </Card>
              <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => navigate({ to: "/dashboard/model-management" })}>
                <CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium">AI Models</CardTitle><Bot className="h-4 w-4 text-muted-foreground" /></CardHeader>
                <CardContent><div className="text-2xl font-bold">{fmt(data.models.total)}</div><p className="text-xs text-muted-foreground">{fmt(data.models.enabled)} enabled</p></CardContent>
              </Card>
              <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => navigate({ to: "/dashboard/files" })}>
                <CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium">Storage</CardTitle><HardDrive className="h-4 w-4 text-muted-foreground" /></CardHeader>
                <CardContent><div className="text-2xl font-bold">{fmt(data.storage.totalFiles)} files</div><p className="text-xs text-muted-foreground">{data.storage.totalSizeGB} GB total</p></CardContent>
              </Card>
            </div>

            <div>
              <h2 className="text-lg font-semibold mb-3">Quick Links</h2>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: "Users", href: "/dashboard/users" },
                  { label: "Canvas", href: "/dashboard/canvas" },
                  { label: "Jobs", href: "/dashboard/jobs" },
                  { label: "Wallets", href: "/dashboard/wallets" },
                  { label: "Projects", href: "/dashboard/projects" },
                  { label: "Subscriptions", href: "/dashboard/subscriptions" },
                  { label: "Earnings", href: "/dashboard/earnings" },
                  { label: "Files", href: "/dashboard/files" },
                  { label: "Analytics", href: "/dashboard/analytics/models" },
                  { label: "Audit Logs", href: "/dashboard/audit-logs" },
                ].map((link) => (
                  <Badge key={link.href} variant="outline" className="cursor-pointer hover:bg-accent px-3 py-1.5 text-sm" onClick={() => navigate({ to: link.href })}>{link.label}</Badge>
                ))}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
